import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
import API_CONFIG, { API_URLS } from '../config/api';
import { FaEdit, FaTrash } from 'react-icons/fa';

const EventMaster = () => {
  const [events, setEvents] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    eventName: '',
    startDate: '',
    endDate: '',
    description: '',
    rules: '',
    location: '118- Ward No.2, Kabir Nagar, Raipur',
    eventType: 'upcoming'
  });
  const [eventPhoto, setEventPhoto] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const fileInputRef = useRef(null);

  // Status configuration
  const STATUS_CONFIG = useMemo(() => ({
    upcoming: { label: 'Upcoming', color: 'bg-yellow-100 text-yellow-800', icon: '📅' },
    ongoing: { label: 'Ongoing', color: 'bg-blue-100 text-blue-800', icon: '🔄' },
    completed: { label: 'Complete', color: 'bg-green-100 text-green-800', icon: '✅' },
    abandoned: { label: 'Abandoned', color: 'bg-red-100 text-red-800', icon: '❌' },
    past: { label: 'Past', color: 'bg-gray-100 text-gray-800', icon: '📚' }
  }), []);

  // Fetch events with error handling and retry
  const fetchEvents = useCallback(async (retryCount = 0) => {
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem('adminToken');
      
      if (!token) {
        setError('Authentication required. Please login again.');
        return;
      }

      const response = await axios.get(API_URLS.EVENT_LIST, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: API_CONFIG.TIMEOUT
      });
      
      setEvents(response.data || []);
    } catch (error) {
      // // console.log('Failed to fetch events:', error);
      
      if (retryCount < 2) {
        setTimeout(() => fetchEvents(retryCount + 1), 1000);
      } else {
        setError('Failed to load events. Please refresh the page.');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  // Load events on component mount
  useEffect(() => {
    fetchEvents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // fetchEvents is stable, no need to include in dependencies

  // Handle form submission with better validation
  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    
    // Enhanced validation
    if (!formData.eventName.trim()) {
      setError('Event name is required');
      return;
    }
    
    if (!formData.startDate || !formData.endDate) {
      setError('Start date and end date are required');
      return;
    }
    
    // Date validation - current date to 2030
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Reset time to start of day
    
    const startDate = new Date(formData.startDate);
    const endDate = new Date(formData.endDate);
    const maxDate = new Date('2030-12-31');
    
    // Check if start date is before today
    if (startDate < today) {
      setError('Start date cannot be before today');
      return;
    }
    
    // Check if start date is after 2030
    if (startDate > maxDate) {
      setError('Start date cannot be after 2030');
      return;
    }
    
    // Check if end date is before today
    if (endDate < today) {
      setError('End date cannot be before today');
      return;
    }
    
    // Check if end date is after 2030
    if (endDate > maxDate) {
      setError('End date cannot be after 2030');
      return;
    }
    
    if (startDate > endDate) {
      setError('End date must be after start date');
      return;
    }

    try {
      setUploading(true);
      setError(null);
      
      const token = localStorage.getItem('adminToken');
      if (!token) {
        setError('Authentication required. Please login again.');
        return;
      }
      
      const formDataToSend = new FormData();
      
      // Add form data - ensure all fields are included
      formDataToSend.append('eventName', formData.eventName);
      
      // Format dates properly for backend
      const formatDateForBackend = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date.toISOString();
      };
      
      formDataToSend.append('startDate', formatDateForBackend(formData.startDate));
      formDataToSend.append('endDate', formatDateForBackend(formData.endDate));
      formDataToSend.append('description', formData.description || '');
      formDataToSend.append('rules', formData.rules || '');
      formDataToSend.append('location', formData.location || '118- Ward No.2, Kabir Nagar, Raipur');
      formDataToSend.append('eventType', formData.eventType || 'upcoming');
      
      // Add photo if selected
      if (eventPhoto) {
        formDataToSend.append('photo', eventPhoto);
      }
      
      if (editingId) {
        // Update existing event
        // console.log('Updating event with ID:', editingId);
        // console.log('Form data:', Object.fromEntries(formDataToSend.entries()));
        
        const response = await axios.put(`/api/events/${editingId}`, formDataToSend, {
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          },
          timeout: API_CONFIG.TIMEOUT
        });
        
        // console.log('Update response:', response.data);
        setSuccessMessage('Event updated successfully! 🎉');
      } else {
        // Create new event
        // console.log('Creating new event');
        // console.log('Form data:', Object.fromEntries(formDataToSend.entries()));
        
        const response = await axios.post('/api/events', formDataToSend, {
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          },
          timeout: API_CONFIG.TIMEOUT
        });
        
        // console.log('Create response:', response.data);
        setSuccessMessage('Event created successfully! 🎉');
      }
      
      // Reset form and refresh data
      setFormData({
        eventName: '',
        startDate: '',
        endDate: '',
        description: '',
        rules: '',
        location: '118- Ward No.2, Kabir Nagar, Raipur',
        eventType: 'upcoming'
      });
      setEventPhoto(null);
      setEditingId(null);
      setShowForm(false);
      
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      
      await fetchEvents();
      
      // Dispatch event to notify other components
      window.dispatchEvent(new Event('eventUpdated'));
      
      // Auto-clear success message
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      // console.log('Failed to save event:', error);
      // console.log('Error response:', error.response?.data);
      
      if (error.response?.status === 401) {
        setError('Session expired. Please login again.');
      } else if (error.response?.data?.error) {
        setError(error.response.data.error);
      } else {
        setError('Failed to save event. Please try again.');
      }
    } finally {
      setUploading(false);
    }
  }, [formData, eventPhoto, editingId, fetchEvents]);



  // Handle delete with confirmation
  const handleDelete = useCallback(async (eventId, eventName) => {
    if (!window.confirm(`Are you sure you want to delete "${eventName}"? This action cannot be undone.`)) {
      return;
    }

    try {
      setLoading(true);
      const token = localStorage.getItem('adminToken');
      
      if (!token) {
        setError('Authentication required. Please login again.');
        return;
      }
      
              await axios.delete(`/api/events/${eventId}`, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: API_CONFIG.TIMEOUT
      });
      
      setSuccessMessage(`Event "${eventName}" deleted successfully! 🗑️`);
      fetchEvents();
      
      // Auto-clear success message
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      // // console.log('Failed to delete event:', error);
      
      if (error.response?.status === 401) {
        setError('Session expired. Please login again.');
      } else {
        setError('Failed to delete event. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  }, []); // Remove fetchEvents to prevent circular dependency

  // Handle edit with form population
  const handleEdit = useCallback((event) => {
    // console.log('Editing event:', event);
    
    // Format dates properly
    const formatDateForInput = (dateString) => {
      if (!dateString) return '';
      const date = new Date(dateString);
      // Handle timezone issues by using local date
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };
    
    setFormData({
      eventName: event.eventName || event.title || '',
      startDate: formatDateForInput(event.startDate || event.date),
      endDate: formatDateForInput(event.endDate || event.date),
      description: event.description || '',
      rules: event.rules || event.description || '',
      location: event.location || '118- Ward No.2, Kabir Nagar, Raipur',
      eventType: event.eventType || 'upcoming'
    });
    setEditingId(event._id || event.id);
    setShowForm(true);
    
    // Scroll to form
    setTimeout(() => {
      document.querySelector('.event-form')?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  }, []);

  // Handle cancel edit
  const handleCancelEdit = useCallback(() => {
    setFormData({
      eventName: '',
      startDate: '',
      endDate: '',
      description: '',
      rules: '',
      location: '118- Ward No.2, Kabir Nagar, Raipur',
      eventType: 'upcoming'
    });
    setEventPhoto(null);
    setEditingId(null);
    setShowForm(false);
    
    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, []);

  // Handle photo change with validation
  const handlePhotoChange = useCallback((e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file size (5MB limit)
      if (file.size > 5 * 1024 * 1024) {
        setError('File size must be less than 5MB');
        e.target.value = '';
        return;
      }
      
      // Validate file type
      const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg', 'image/gif', 'image/webp'];
      if (!allowedTypes.includes(file.type)) {
        setError('Please select a valid image file (JPEG, PNG, GIF, WebP)');
        e.target.value = '';
        return;
      }
      
      setEventPhoto(file);
      setError(null);
    }
  }, []);

  // Handle form change
  const handleFormChange = useCallback((e) => {
    const { name, value } = e.target;
    setFormData(prev => {
      const newData = {
        ...prev,
        [name]: value
      };
      
      // If start date changes, update end date minimum
      if (name === 'startDate' && value) {
        const startDate = new Date(value);
        const endDate = new Date(prev.endDate);
        
        // If end date is before new start date, clear it
        if (prev.endDate && endDate < startDate) {
          newData.endDate = '';
        }
      }
      
      return newData;
    });
    
    // Clear error when user starts typing
    if (error) {
      setError(null);
    }
  }, [error]);

  // Handle status change with optimistic update
  const handleStatusChange = useCallback(async (event, newStatus) => {
    const originalStatus = event.eventType;
    
    // Optimistic update
    setEvents(prev => prev.map(e => 
      e._id === event._id ? { ...e, eventType: newStatus } : e
    ));

    try {
      const token = localStorage.getItem('adminToken');
      
      if (!token) {
        setError('Authentication required. Please login again.');
        return;
      }
      
              await axios.put(`/api/events/${event._id}`, {
        ...event,
        eventType: newStatus
      }, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: API_CONFIG.TIMEOUT
      });
      
      setSuccessMessage(`Event status updated to ${STATUS_CONFIG[newStatus]?.label}! ✨`);
      
      // Dispatch event to notify other components
      window.dispatchEvent(new Event('eventUpdated'));
      
      setTimeout(() => setSuccessMessage(''), 2000);
    } catch (error) {
      // // console.log('Failed to update event status:', error);
      
      // Revert optimistic update
      setEvents(prev => prev.map(e => 
        e._id === event._id ? { ...e, eventType: originalStatus } : e
      ));
      
      if (error.response?.status === 401) {
        setError('Session expired. Please login again.');
      } else {
        setError('Failed to update event status. Please try again.');
      }
    }
  }, [STATUS_CONFIG]);

  // Format date with better formatting
  const formatDate = useCallback((dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  }, []);

  // Get photo URL with fallback - FIXED IMAGE PATH ISSUE
  const getPhotoUrl = useCallback((photoUrl) => {
    if (!photoUrl) return '/images/heroSection_img.jpg';
    if (photoUrl.startsWith('https')) return photoUrl;
    if (photoUrl.startsWith('/uploads/')) return photoUrl;
    return `/uploads/events/${photoUrl}`;
  }, []);

  // Status options with icons
  const statusOptions = useMemo(() => 
    Object.entries(STATUS_CONFIG).map(([value, config]) => ({
      value,
      label: config.label,
      color: config.color,
      icon: config.icon
    })), [STATUS_CONFIG]
  );

  // Filtered events with search
  const filteredEvents = useMemo(() => {
    let filtered = events;
    
    // Filter by status
    if (selectedStatus) {
      filtered = filtered.filter(event => event.eventType === selectedStatus);
    }
    
    // Filter by search term
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(event => 
        event.eventName?.toLowerCase().includes(term) ||
        event.location?.toLowerCase().includes(term) ||
        event.description?.toLowerCase().includes(term)
      );
    }
    
    return filtered;
  }, [events, selectedStatus, searchTerm]);

  // Clear message
  const clearMessage = useCallback(() => {
    setSuccessMessage('');
    setError(null);
  }, []);

  // Get status counts (commented out as not currently used)
  // const statusCounts = useMemo(() => {
  //   const counts = {};
  //   Object.keys(STATUS_CONFIG).forEach(status => {
  //     counts[status] = events.filter(event => event.eventType === status).length;
  //   });
  //   return counts;
  // }, [events, STATUS_CONFIG]);

  return (
    <div>
      {/* Success message */}
      {successMessage && (
        <div className="mb-4 p-4 bg-green-100 border border-green-400 text-green-700 rounded flex items-center justify-between">
          <span>{successMessage}</span>
          <button
            onClick={clearMessage}
            className="text-green-700 hover:text-green-900 font-bold text-lg"
          >
            ×
          </button>
        </div>
      )}

      {/* Error message */}
      {error && (
        <div className="mb-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded flex items-center justify-between">
          <span>{error}</span>
          <button
            onClick={clearMessage}
            className="text-red-700 hover:text-red-900 font-bold text-lg"
          >
            ×
          </button>
        </div>
      )}

      {/* Event Form Section */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden mb-6">
        <div className="px-8 py-6 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-800">Event Management</h3>
        </div>
        
        {showForm ? (
          <div className="px-8 py-6 event-form">
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Event Name *
                  </label>
                  <input
                    type="text"
                    name="eventName"
                    value={formData.eventName}
                    onChange={handleFormChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter event name"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Location
                  </label>
                  <input
                    type="text"
                    name="location"
                    value={formData.location}
                    onChange={handleFormChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter event location"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Start Date *
                  </label>
                  <input
                    type="date"
                    name="startDate"
                    value={formData.startDate}
                    onChange={handleFormChange}
                    min={new Date().toISOString().split('T')[0]}
                    max="2030-12-31"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">Date must be between today and 2030</p>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    End Date *
                  </label>
                  <input
                    type="date"
                    name="endDate"
                    value={formData.endDate}
                    onChange={handleFormChange}
                    min={formData.startDate || new Date().toISOString().split('T')[0]}
                    max="2030-12-31"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">Date must be between today and 2030</p>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Event Type
                  </label>
                  <select
                    name="eventType"
                    value={formData.eventType}
                    onChange={handleFormChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {statusOptions.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.icon} {option.label}
                      </option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Event Photo
                  </label>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handlePhotoChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">Max size: 5MB. Supported: JPEG, PNG, GIF, WebP</p>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description
                </label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleFormChange}
                  rows="3"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter event description"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Rules & Regulations
                </label>
                <textarea
                  name="rules"
                  value={formData.rules}
                  onChange={handleFormChange}
                  rows="12"
                  maxLength="10000"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 resize-y min-h-[200px]"
                  placeholder="Enter detailed event rules and regulations (up to 10,000 characters). Include:
• Eligibility criteria
• Tournament format  
• Prize distribution
• Code of conduct
• Registration requirements
• Match rules
• Dispute resolution
• Any other important guidelines"
                />
                <div className="flex justify-between items-center mt-1">
                  <p className="text-xs text-gray-500">
                    💡 Tip: Use bullet points, numbered lists, and clear sections for better readability
                  </p>
                  <p className="text-xs text-gray-500">
                    {formData.rules.length}/10,000 characters
                  </p>
                </div>
              </div>
              
              <div className="flex space-x-4">
                <button
                  type="submit"
                  disabled={uploading}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm flex items-center"
                >
                  {uploading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Saving...
                    </>
                  ) : (
                    <>
                      {editingId ? 'Update Event' : 'Create Event'}
                    </>
                  )}
                </button>
                
                <button
                  type="button"
                  onClick={handleCancelEdit}
                  className="px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600 text-sm"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        ) : (
          <div className="px-8 py-6">
            <button
              onClick={() => setShowForm(true)}
              className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 text-sm flex items-center"
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              Add New Event
            </button>
          </div>
        )}
      </div>

      {/* Search and Filter Section */}
      <div className="mb-6 space-y-4">
        {/* Status Filter */}
        <div className="mb-6">
          {/* Search Bar */}
          <div className="relative mb-4">
            <input
              type="text"
              placeholder="Search events by name, location, or description..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <svg
              className="absolute left-3 top-2.5 h-5 w-5 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>

          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setSelectedStatus(null)}
              className={`px-3 py-1.5 rounded-md text-sm ${
                selectedStatus === null
                  ? 'bg-blue-500 text-white'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              All Events ({events.length})
            </button>
            {statusOptions.map(option => {
              const count = events.filter(event => event.eventType === option.value).length;
              return (
                <button
                  key={option.value}
                  onClick={() => setSelectedStatus(option.value)}
                  className={`px-3 py-1.5 rounded-md text-sm ${
                    selectedStatus === option.value
                      ? 'bg-blue-500 text-white'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  {option.label} ({count})
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Events Table */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <div className="px-8 py-6 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-800">
            Events ({filteredEvents.length})
            {searchTerm && (
              <span className="text-sm font-normal text-gray-500 ml-2">
                - Search results for "{searchTerm}"
              </span>
            )}
          </h3>
        </div>
        
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Photo
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider max-w-[200px]">
                  Event Name
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Location
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Dates
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan="6" className="px-4 py-8 text-center text-gray-500">
                    <div className="flex items-center justify-center">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mr-2"></div>
                      Loading events...
                    </div>
                  </td>
                </tr>
              ) : filteredEvents.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-4 py-8 text-center text-gray-500">
                    <div className="flex flex-col items-center">
                      <svg className="w-12 h-12 text-gray-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      <p className="text-gray-500">
                        {searchTerm ? 'No events found matching your search' : 'No events found'}
                      </p>
                      <p className="text-sm text-gray-400">
                        {searchTerm ? 'Try adjusting your search terms' : 'Create your first event to get started'}
                      </p>
                    </div>
                  </td>
                </tr>
              ) : (
                filteredEvents.map(event => (
                  <tr key={event._id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3 whitespace-nowrap">
                      <img
                        src={getPhotoUrl(event.photoUrl)}
                        alt={event.eventName}
                        className="w-12 h-12 object-cover rounded-lg border border-gray-200 hover:scale-105 transition-transform cursor-pointer"
                        onError={(e) => {
                          // Prevent infinite loop by checking if we're already on fallback
                          if (!e.target.src.includes('/images/heroSection_img.jpg')) {
                            e.target.src = '/images/heroSection_img.jpg';
                          } else {
                            // If fallback also fails, hide the image and show placeholder
                            e.target.style.display = 'none';
                            const placeholder = document.createElement('div');
                            placeholder.className = 'w-12 h-12 bg-gray-200 rounded-lg border border-gray-200 flex items-center justify-center';
                            placeholder.innerHTML = `
                              <svg class="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 002 2z" />
                              </svg>
                            `;
                            e.target.parentNode.appendChild(placeholder);
                          }
                        }}
                        onClick={() => {
                          // Open image in new tab
                          window.open(getPhotoUrl(event.photoUrl), '_blank');
                        }}
                      />
                    </td>
                    <td 
                      className="px-4 py-3 max-w-[200px] truncate cursor-help" 
                      title={event.eventName || 'N/A'}
                    >
                      <div className="text-sm font-medium text-gray-900 truncate">{event.eventName}</div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                      {event.location}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                      <div>
                        {(() => {
                          // Format dates properly
                          const formatDate = (dateString) => {
                            if (!dateString) return 'No Date';
                            const date = new Date(dateString);
                            return date.toLocaleDateString('en-GB', {
                              day: '2-digit',
                              month: '2-digit',
                              year: 'numeric'
                            });
                          };
                          
                          const startDate = formatDate(event.startDate);
                          const endDate = formatDate(event.endDate);
                          
                          return `${startDate} to ${endDate}`;
                        })()}
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <select
                        value={event.eventType}
                        onChange={(e) => handleStatusChange(event, e.target.value)}
                        className={`text-xs px-2 py-1 rounded-full border ${STATUS_CONFIG[event.eventType]?.color || 'bg-gray-100 text-gray-800'} hover:shadow-sm transition-shadow`}
                      >
                        {statusOptions.map(option => (
                          <option key={option.value} value={option.value}>
                            {option.icon} {option.label}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-center text-sm font-medium">
                      <div className="flex items-center justify-center gap-2">
                        <div
                          onClick={() => handleEdit(event)}
                          className="bg-blue-100 hover:bg-blue-200 text-blue-600 w-8 h-8 rounded-md transition-colors duration-200 flex items-center justify-center cursor-pointer"
                          title="Edit event"
                        >
                          <FaEdit className="w-4 h-4" />
                        </div>
                        <div
                          onClick={() => handleDelete(event._id, event.eventName)}
                          className="bg-yellow-100 hover:bg-yellow-200 text-orange-600 w-8 h-8 rounded-md transition-colors duration-200 flex items-center justify-center cursor-pointer"
                          title="Delete event"
                        >
                          <FaTrash className="w-4 h-4" />
                        </div>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default EventMaster;